/**
 * Endpoints de **Logística → Gastos** (`/api/logistica/gastos`).
 *
 * El listado va paginado en el servidor con los filtros del panel anterior
 * (fechas, tipo y monto mínimo) más el buscador libre, y la edición y la
 * inhabilitación se piden por gasto.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type { GastoEditable, GastosFiltros, GastosRespuesta } from './gastos.types';

export const gastosKeys = {
  lista: (filtros: GastosFiltros) => ['logistica', 'gastos', filtros] as const,
};

/** Arma la cadena de consulta del listado. */
function cadenaDe(filtros: GastosFiltros): string {
  const parametros = new URLSearchParams();

  if (filtros.desde) parametros.set('desde', filtros.desde);
  if (filtros.hasta) parametros.set('hasta', filtros.hasta);
  if (filtros.tipo) parametros.set('tipo_gasto', filtros.tipo);
  if (filtros.montoMin) parametros.set('monto_min', filtros.montoMin);
  if (filtros.search) parametros.set('search', filtros.search);
  if (filtros.orden) parametros.set('orden', filtros.orden);
  if (filtros.direccion) parametros.set('direccion', filtros.direccion);
  if (filtros.page) parametros.set('page', String(filtros.page));
  if (filtros.perPage) parametros.set('per_page', String(filtros.perPage));

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const gastosApi = {
  listar: (filtros: GastosFiltros = {}, signal?: AbortSignal): Promise<GastosRespuesta> =>
    apiClient.get<GastosRespuesta>(`/api/logistica/gastos${cadenaDe(filtros)}`, { signal }),

  editar: (id: number, gasto: GastoEditable): Promise<{ message: string }> =>
    apiClient.put<{ message: string }>(`/api/logistica/gastos/${id}`, {
      body: { descripcion: gasto.descripcion, cantidad: Number(gasto.cantidad) },
    }),

  eliminar: (id: number): Promise<{ message: string; id: number }> =>
    apiClient.delete<{ message: string; id: number }>(`/api/logistica/gastos/${id}`),
};

/** Gastos del filtro pedido. */
export function useGastos(filtros: GastosFiltros = {}) {
  return useQuery({
    queryKey: gastosKeys.lista(filtros),
    queryFn: ({ signal }) => gastosApi.listar(filtros, signal),
  });
}

/** Guarda los cambios de un gasto y refresca el listado. */
export function useEditarGasto() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, gasto }: { id: number; gasto: GastoEditable }) =>
      gastosApi.editar(id, gasto),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['logistica', 'gastos'] });
    },
  });
}

/** Inhabilita un gasto y refresca el listado. */
export function useEliminarGasto() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => gastosApi.eliminar(id),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['logistica', 'gastos'] });
    },
  });
}
